import React, { useState } from 'react';
import { Card, CardBody, CardFooter, Col } from 'reactstrap';
import LazyImage from '../../lazyload/lazy-image/lazy-image.component';
import { connect } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {
  setEditInfoModalData,
  setEditMenuEntryCategoryModalData,
  setInfoModalData,
  setIsCreateInfoModalOpen,
  setIsEditInfoModalOpen,
  setIsEditMenuEntryCategoryModalOpen,
  setIsInfoModalOpen,
} from '../../../../redux/modal/modal.actions';
import * as Icon from 'react-feather';
import DisplayTypeField from '../../form/fields/DisplayTypeField';
import { fetchMenuEntries } from '../../../../redux/store/store.actions';
import {
  getLocalizedDescription,
  getLocalizedName,
  getLocalizedTitle,
  trimText,
} from '../../../../utility/util/util.service';
import { useIntl } from 'react-intl';
import SkeletonLoader from '../../lazyload/skeleton-loader/skeleton-loader.component';
import RemoveConfirmationModal from '../../modals/remove-confirmation/remove-confirmation.modal';
import { removeInfo } from './info-card.service';

const InfoCard = ({
  menuEntry,
  store,
  isAdminMode,

  setIsInfoModalOpen,
  fetchMenuEntries,
  setEditInfoModalData,
  setIsEditInfoModalOpen,
  setInfoModalData,
  setEditMenuEntryCategoryModalData,
  setIsEditMenuEntryCategoryModalOpen,
}) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const intl = useIntl();
  const displayImageOnly = menuEntry?.displayType === 'image_only';
  const displayTextOnly = menuEntry?.displayType === 'text_only';
  const displayFull = menuEntry?.displayType === 'full';
  const displayHidden = menuEntry?.displayType === 'hidden';
  const displayImage = displayFull || displayImageOnly;
  const displayText = displayFull || displayTextOnly;
  const title = getLocalizedTitle(menuEntry, intl.locale);
  const itemDescription = getLocalizedDescription(menuEntry, intl.locale);
  const itemHasImages = menuEntry?.image ? true : false;
  const itemImageVisible = !!(itemHasImages && displayImage);
  const itemNameVisible = !!(title && displayText);

  const openInfoModal = () => {
    setInfoModalData(menuEntry);
    setIsInfoModalOpen(true);
  };
  const openEditModal = () => {
    setEditInfoModalData(menuEntry);
    setIsEditInfoModalOpen(true);
  };

  const openEditCategoryModal = () => {
    setEditMenuEntryCategoryModalData({
      info: menuEntry,
    });
    setIsEditMenuEntryCategoryModalOpen(true);
  };

  const itemImageClassNames = classnames('h-100 mh-10 img-cover-center', {
    'card-img': !isAdminMode && (displayImageOnly || !itemNameVisible),
    'card-img-top':
      (!displayImageOnly && itemNameVisible) ||
      (isAdminMode && (displayImageOnly || !itemNameVisible)),
  });
  const itemNameClassNames = classnames('px-2 py-1');
  const itemNameSpanClassNames = classnames('font-medium-2 m-0 d-inline', {
    'font-italic text-muted':
      !itemNameVisible && isAdminMode && !itemImageVisible,
  });

  const extractTextFromHTML = (htmlString) => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlString;
    return tempElement.textContent || tempElement.innerText || '';
  };

  const extractedText = extractTextFromHTML(itemDescription);

  return (
    <>
      <Col xl="4" lg="4" md="4" sm="6" xs="12" className="cursor-pointer">
        <Card data-testid="infoCard">
          {menuEntry ? (
            itemImageVisible && (
              <div className="d-flex flex-grow-1" onClick={openInfoModal}>
                <LazyImage
                  image={menuEntry?.image}
                  variant="menuEntry"
                  className={itemImageClassNames}
                  placeholderContainerClassNames="h-100 mh-10 w-100 line-height-1 d-block"
                  placeholderClassNames="mh-10"
                />
              </div>
            )
          ) : (
            <SkeletonLoader
              height="100%"
              containerClassName="mh-10 card-img-top"
              style={{ top: '-3px' }}
            />
          )}
          {menuEntry ? (
            (itemNameVisible || (isAdminMode && !itemImageVisible)) && (
              <CardBody onClick={openInfoModal} className={itemNameClassNames}>
                <div className="d-flex align-items-center">
                  <h1 className={itemNameSpanClassNames} data-testid="infoName">
                    {trimText(title, 40, ' ...')}
                  </h1>
                </div>
                {itemNameVisible && itemDescription && (
                  <span className="d-block font-small-3 mt-50">
                    {trimText(extractedText, 300, ' ...')}
                  </span>
                )}
              </CardBody>
            )
          ) : (
            <CardBody className={itemNameClassNames}>
              <div className="d-block">
                <span className={itemNameSpanClassNames}>
                  <SkeletonLoader width="80%" />
                </span>
              </div>
              <span className="d-block font-small-3 mt-50">
                <SkeletonLoader count={2} />
              </span>
            </CardBody>
          )}
          {menuEntry && isAdminMode && (
            <CardFooter className="bg-white p-1 d-flex justify-content-around align-items-center">
              <span onClick={() => setIsRemoveModalOpen(true)}>
                <Icon.Trash2
                  size={22}
                  className="danger"
                  data-testid="removeItemButton"
                />
              </span>
              <span onClick={openEditModal}>
                <Icon.Edit id="editBtn" size={22} className="secondary" />
              </span>
              <span onClick={openEditCategoryModal}>
                <Icon.Folder
                  size={22}
                  id="editCategory"
                  className="secondary"
                />
              </span>
              <DisplayTypeField
                object={menuEntry}
                onSuccessCallback={() =>
                  fetchMenuEntries(store.menu.id, isAdminMode, undefined, store.id)
                }
              />
            </CardFooter>
          )}
        </Card>
      </Col>
      {isAdminMode && (
        <>
          <RemoveConfirmationModal
            isOpen={isRemoveModalOpen}
            closeModal={() => setIsRemoveModalOpen(false)}
            titleId="confirm"
            messageId="delete_confirmation_item"
            handleRemoval={() =>
              removeInfo(menuEntry?.id, store?.id, () =>
                fetchMenuEntries(store.menu.id, isAdminMode, undefined, store.id),
              )
            }
          />
        </>
      )}
    </>
  );
};

InfoCard.propTypes = {
  menuEntry: PropTypes.object,
  slug: PropTypes.string,
  onClick: PropTypes.func,
  isAdminMode: PropTypes.bool,
  store: PropTypes.object,
  fetchMenuEntries: PropTypes.func,
  setIsEditCategoryModalOpen: PropTypes.func,
  setEditCategoryModalData: PropTypes.func,
};

const mapStateToProps = (state) => ({
  store: state.store.store,
});

export default connect(mapStateToProps, {
  setIsInfoModalOpen,
  fetchMenuEntries,
  setEditInfoModalData,
  setIsEditInfoModalOpen,
  setInfoModalData,
  setEditMenuEntryCategoryModalData,
  setIsEditMenuEntryCategoryModalOpen,
})(InfoCard);
